import { useMemo, useRef } from "react";
import * as THREE from "three";
import type { RoadSegment } from "../../utils/clusteringAlgorithm";

interface RoadMeshRendererProps {
  roadSegments: RoadSegment[];
  maxSegments?: number; // 성능을 위해 제한
  opacity?: number; // 투명도 (0~1)
}

/**
 * 도로를 실제 3D 메시로 렌더링하는 컴포넌트
 *
 * 특징:
 * - 얇은 박스 형태로 두께감 표현
 * - GPU frustum culling
 */
export function RoadMeshRenderer({
  roadSegments,
  maxSegments = 500, // 성능 제한
  opacity = 1.0,
}: RoadMeshRendererProps) {
  const groupRef = useRef<THREE.Group>(null);

  // 도로 메시 생성 (두께 추가)
  const roadGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions: number[] = [];
    const indices: number[] = [];
    const colors: number[] = [];

    // 도로 색상 (다크 모드)
    const roadColor = new THREE.Color(0.85, 0.85, 0.85);

    // 도로 두께 (Y축 높이)
    const roadHeight = 0.05;

    const segments = roadSegments.slice(0, maxSegments);
    let vertexIndex = 0;

    segments.forEach((segment) => {
      const { x1, z1, x2, z2, width } = segment;
      const halfWidth = width * 0.5;
      const yBottom = 0.0;
      const yTop = roadHeight;

      // 방향 벡터
      const dx = x2 - x1;
      const dz = z2 - z1;
      const length = Math.sqrt(dx * dx + dz * dz);

      if (length < 0.001) return;

      // 정규화된 방향 벡터
      const dirX = dx / length;
      const dirZ = dz / length;

      // 수직 벡터 (도로 폭 방향)
      const perpX = -dirZ * halfWidth;
      const perpZ = dirX * halfWidth;

      // 연결 부분을 위해 양끝을 확장
      const extendAmount = width / 2;
      const extendedX1 = x1 - dirX * extendAmount;
      const extendedZ1 = z1 - dirZ * extendAmount;
      const extendedX2 = x2 + dirX * extendAmount;
      const extendedZ2 = z2 + dirZ * extendAmount;

      // 8개 정점 (박스 형태: 위 4개 + 아래 4개)
      // 상단 면 (y = yTop)
      const topP1 = [extendedX1 + perpX, yTop, extendedZ1 + perpZ];
      const topP2 = [extendedX1 - perpX, yTop, extendedZ1 - perpZ];
      const topP3 = [extendedX2 + perpX, yTop, extendedZ2 + perpZ];
      const topP4 = [extendedX2 - perpX, yTop, extendedZ2 - perpZ];

      // 하단 면 (y = yBottom) - 측면 렌더링용
      const botP1 = [extendedX1 + perpX, yBottom, extendedZ1 + perpZ];
      const botP2 = [extendedX1 - perpX, yBottom, extendedZ1 - perpZ];
      const botP3 = [extendedX2 + perpX, yBottom, extendedZ2 + perpZ];
      const botP4 = [extendedX2 - perpX, yBottom, extendedZ2 - perpZ];

      // 상단 면 (메인 도로 표면)
      positions.push(...topP1, ...topP2, ...topP3, ...topP4);
      indices.push(
        vertexIndex,
        vertexIndex + 1,
        vertexIndex + 2,
        vertexIndex + 1,
        vertexIndex + 3,
        vertexIndex + 2
      );
      colors.push(
        ...roadColor.toArray(),
        ...roadColor.toArray(),
        ...roadColor.toArray(),
        ...roadColor.toArray()
      );
      vertexIndex += 4;

      // 측면 1 (perp+ 방향)
      const sideColor1 = new THREE.Color(0.6, 0.6, 0.6);
      positions.push(...topP1, ...topP3, ...botP1, ...botP3);
      indices.push(
        vertexIndex,
        vertexIndex + 1,
        vertexIndex + 2,
        vertexIndex + 1,
        vertexIndex + 3,
        vertexIndex + 2
      );
      colors.push(
        ...sideColor1.toArray(),
        ...sideColor1.toArray(),
        ...sideColor1.toArray(),
        ...sideColor1.toArray()
      );
      vertexIndex += 4;

      // 측면 2 (perp- 방향)
      const sideColor2 = new THREE.Color(0.5, 0.5, 0.5);
      positions.push(...topP2, ...topP4, ...botP2, ...botP4);
      indices.push(
        vertexIndex,
        vertexIndex + 1,
        vertexIndex + 2,
        vertexIndex + 1,
        vertexIndex + 3,
        vertexIndex + 2
      );
      colors.push(
        ...sideColor2.toArray(),
        ...sideColor2.toArray(),
        ...sideColor2.toArray(),
        ...sideColor2.toArray()
      );
      vertexIndex += 4;
    });

    if (positions.length === 0) {
      return null;
    }

    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    geometry.setIndex(indices);
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    return geometry;
  }, [roadSegments, maxSegments]);

  // 중앙 점선 geometry 생성 (도로 중심선)
  const dashGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions: number[] = [];
    const indices: number[] = [];
    const colors: number[] = [];

    // 점선 색상 (다크 모드)
    const dashColor = new THREE.Color(0.15, 0.15, 0.15);

    const segments = roadSegments.slice(0, maxSegments);
    let vertexIndex = 0;

    // 점선 파라미터
    const dashLength = 0.4;
    const gapLength = 0.3;
    const dashWidth = 0.08;
    const dashY = 0.06; // 도로 두께 위에

    segments.forEach((segment) => {
      const { x1, z1, x2, z2 } = segment;

      const dx = x2 - x1;
      const dz = z2 - z1;
      const length = Math.sqrt(dx * dx + dz * dz);

      if (length < dashLength) return;

      const dirX = dx / length;
      const dirZ = dz / length;

      const perpX = -dirZ * dashWidth * 0.5;
      const perpZ = dirX * dashWidth * 0.5;

      let currentPos = 0;
      while (currentPos + dashLength <= length) {
        const startX = x1 + dirX * currentPos;
        const startZ = z1 + dirZ * currentPos;
        const endX = x1 + dirX * (currentPos + dashLength);
        const endZ = z1 + dirZ * (currentPos + dashLength);

        const dp1 = [startX + perpX, dashY, startZ + perpZ];
        const dp2 = [startX - perpX, dashY, startZ - perpZ];
        const dp3 = [endX + perpX, dashY, endZ + perpZ];
        const dp4 = [endX - perpX, dashY, endZ - perpZ];

        positions.push(...dp1, ...dp2, ...dp3, ...dp4);

        indices.push(
          vertexIndex,
          vertexIndex + 1,
          vertexIndex + 2,
          vertexIndex + 1,
          vertexIndex + 3,
          vertexIndex + 2
        );

        colors.push(
          ...dashColor.toArray(),
          ...dashColor.toArray(),
          ...dashColor.toArray(),
          ...dashColor.toArray()
        );

        vertexIndex += 4;
        currentPos += dashLength + gapLength;
      }
    });

    if (positions.length === 0) {
      return null;
    }

    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    geometry.setIndex(indices);
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    return geometry;
  }, [roadSegments, maxSegments]);

  if (!roadGeometry) {
    return null;
  }

  return (
    <group ref={groupRef}>
      <mesh geometry={roadGeometry} frustumCulled={true} renderOrder={1}>
        <meshBasicMaterial
          vertexColors={true}
          toneMapped={false}
          depthWrite={opacity === 1}
          depthTest={true}
          side={THREE.DoubleSide}
          transparent={opacity < 1}
          opacity={opacity}
          stencilWrite={true}
          stencilFunc={THREE.EqualStencilFunc}
          stencilRef={0}
          stencilFail={THREE.KeepStencilOp}
          stencilZFail={THREE.KeepStencilOp}
          stencilZPass={THREE.KeepStencilOp}
        />
      </mesh>
      {/* 중앙 점선 (도로 위에 렌더링) */}
      {dashGeometry && (
        <mesh geometry={dashGeometry} frustumCulled={true} renderOrder={2}>
          <meshBasicMaterial
            vertexColors={true}
            toneMapped={false}
            depthWrite={opacity === 1}
            depthTest={true}
            side={THREE.DoubleSide}
            transparent={opacity < 1}
            opacity={opacity}
            stencilWrite={true}
            stencilFunc={THREE.EqualStencilFunc}
            stencilRef={0}
            stencilFail={THREE.KeepStencilOp}
            stencilZFail={THREE.KeepStencilOp}
            stencilZPass={THREE.KeepStencilOp}
          />
        </mesh>
      )}
    </group>
  );
}
